"use client";

import { FC, useEffect, useRef, useState } from "react";
import clsx from "clsx";

// -----------------------------------------------------------------------------
// StatCounter — counts a stat up from 0 when it scrolls into view (easeOutCubic),
// then snaps to the exact source string so suffixes/format are preserved
// ("1,200+", "98%", "14+", "24/7"). Honors prefers-reduced-motion (shows the
// final value instantly). Reuses the IntersectionObserver idiom from
// RevealOnScroll so motion across the site stays consistent.
// -----------------------------------------------------------------------------

export type IStatCounterProps = {
  value: string;
  className?: string;
  durationMs?: number;
};

const parse = (value: string) => {
  const match = value.match(/^(\D*)([\d,.]+)(.*)$/);
  if (!match)
    return {
      prefix: "",
      num: null as number | null,
      suffix: value,
      decimals: 0,
    };
  const [, prefix, rawNum, suffix] = match;
  const clean = rawNum.replace(/,/g, "");
  const decimals = clean.includes(".") ? clean.split(".")[1].length : 0;
  return { prefix, num: parseFloat(clean), suffix, decimals };
};

const StatCounter: FC<IStatCounterProps> = ({
  value,
  className,
  durationMs = 1600,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const { prefix, num, suffix, decimals } = parse(value);
  const [display, setDisplay] = useState(
    num === null ? value : `${prefix}0${suffix}`,
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || num === null) {
      setDisplay(value);
      return;
    }

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDisplay(value);
      return;
    }

    const fmt = (n: number) =>
      `${prefix}${n.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`;

    let raf = 0;
    let start = 0;
    const tick = (t: number) => {
      if (!start) start = t;
      const p = Math.min((t - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setDisplay(fmt(num * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value); // exact source string at the end
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          raf = requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [value, num, prefix, suffix, decimals, durationMs]);

  return (
    <span ref={ref} className={clsx("tabular-nums", className)}>
      {display}
    </span>
  );
};

export default StatCounter;
